// RecordAlarmSettingBox.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import SwitchBox from './SwitchBox';
import styled from 'styled-components';
import imgDay01 from '../../images/common/i_day_morning_02.svg';
import imgDay02 from '../../images/common/i_day_lunch_02.svg';
import imgDay03 from '../../images/common/i_day_night_02.svg';
import CustomTimePicker from '../datepicker/CustomTimePicker';
import { getPillEraTime } from '../../constants/pillEraTime';
import moduleStyle from '../../pages/user/totalmenu/appsetting/RecordSettingDetail.module.css';

const AlarmListBox = styled.div`
    display: block;
    margin-top: var(--sp-20);
    & > ul {
        display: flex;
        flex-direction: column;
        gap: var(--sp-20);
    }
`;

const HeadBox = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const ControlBox = styled.div`
    display: flex;
    align-items: center;
    gap: var(--sp-12);
    & > span {
        font-size: var(--mo-fz-md);
        color: var(--color-black);
        font-weight: var(--fz-weight-700);
    }
    & > .no-alarm {
        color: var(--color-main-gray-30);
    }
`;

const DayBox = styled.div`
    display: flex;
    align-items: center;
    gap: var(--sp-04);
    & span {
        display: flex;
        align-items: center;
        gap: var(--sp-04);
        height: var(--sp-18);
        padding: 0 var(--sp-08);
        border-radius: var(--radius-100);
        background-color: var(--color-main-25);
        font-size: var(--mo-fz-xxsm);
        color: var(--color-main-5);
        font-weight: var(--fz-weight-700);
        &[data-day] {
            padding: 0 var(--sp-08) 0 var(--sp-06);
        }
        &[data-day]::before {
            content: "";
            display: block;
            width: var(--sp-12);
            height: var(--sp-12);
            background: url(${imgDay01}) no-repeat center; transform: scale(1.2);
        }
        &[data-day="2"]::before {
            background: url(${imgDay02}) no-repeat center;
        }
        &[data-day="3"]::before {
            background: url(${imgDay03}) no-repeat center;
        }
    }
`;

const DateBox = styled.span`
    font-size: var(--mo-fz-sm);
    color: var(--color-main-gray-5);
`;

/**
 * SheetOverlay - react-spring-bottom-sheet 대체
 * inline style 기반으로 module.css 의존성 없이 동작
 */
const SheetOverlay = ({ open, onDismiss, children }) => {
    const [visible, setVisible] = useState(false);
    const [animating, setAnimating] = useState(false);

    useEffect(() => {
        if (open) {
            setVisible(true);
            requestAnimationFrame(() => {
                requestAnimationFrame(() => setAnimating(true));
            });
        } else {
            setAnimating(false);
            const timer = setTimeout(() => setVisible(false), 300);
            return () => clearTimeout(timer);
        }
    }, [open]);

    if (!visible) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            zIndex: 1000,
            display: 'flex',
            alignItems: 'flex-end',
            pointerEvents: animating ? 'auto' : 'none',
        }}>
            <div
                onClick={onDismiss}
                style={{
                    position: 'absolute',
                    top: 0, left: 0, right: 0, bottom: 0,
                    background: animating ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0)',
                    transition: 'background 0.3s ease',
                }}
            />
            <div style={{
                position: 'relative',
                width: '100%',
                maxHeight: '90vh',
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                background: '#fff',
                borderRadius: '16px 16px 0 0',
                paddingTop: '16px',
                paddingBottom: 'env(safe-area-inset-bottom, 0)',
                transform: animating ? 'translateY(0)' : 'translateY(100%)',
                transition: 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)',
                zIndex: 1,
            }}>
                {children}
            </div>
        </div>
    );
};

const RecordAlarmSettingBox = ({ dayType, dayLabel, subtitle, time, isOn, period, totDays, handleConfirmTime, handleSwitchChange, alarmTimeOpen, onDismiss }) => {

    const pickerInitTime = useMemo(() => {
        if (time && time.length >= 4) {
            return { hour: parseInt(time.substring(0, 2), 10), minute: parseInt(time.substring(2, 4), 10) };
        }
        return getPillEraTime(dayType);
    }, [time, dayType]);

    const formatTime = (timeStr) => {
        if (!timeStr || timeStr.length < 4) return "";
        const hour = parseInt(timeStr.substring(0, 2), 10);
        const minute = timeStr.substring(2, 4);
        const p = hour >= 12 ? '오후' : '오전';
        let displayHour = hour % 12;
        if (displayHour === 0) displayHour = 12;
        return `${p} ${String(displayHour).padStart(2, '0')}:${minute}`;
    };

    return (
        <>
            <AlarmListBox>
                <HeadBox>
                    <DayBox>
                        <span data-day={dayType}>{dayLabel}</span>
                        {subtitle?.trim() && subtitle.trim().toLowerCase() !== 'null' && (
                            <span>{subtitle.trim()}</span>
                        )}
                    </DayBox>
                    <ControlBox>
                        {isOn && time ? (
                            <span>{formatTime(time)}</span>
                        ) : (
                            <span className="no-alarm">알림 없음</span>
                        )}
                        <SwitchBox toggles={isOn} status={handleSwitchChange} />
                    </ControlBox>
                </HeadBox>
                <DateBox>{period} <b>({totDays}일)</b></DateBox>
            </AlarmListBox>

            <SheetOverlay open={alarmTimeOpen} onDismiss={onDismiss}>
                <CustomTimePicker
                    type="record"
                    titleTxt="알림 시간을 선택해주세요"
                    subTitleTxt="복약 기간동안 설정한 시간에 복약 알림이 울립니다."
                    onClose={onDismiss}
                    onConfirm={handleConfirmTime}
                    initialTime={pickerInitTime}
                    reAlarm
                >
                    <div className={moduleStyle['picker-top-box']} data-type='b'>
                        <div className={moduleStyle['record-hisyory-list']} data-type="b">
                            <div className={moduleStyle['top-box']}>
                                <span data-day={dayType}>{dayLabel}</span>
                                {subtitle?.trim() && subtitle.trim().toLowerCase() !== 'null' && (
                                    <span>{subtitle.trim()}</span>
                                )}
                            </div>
                            <div className={moduleStyle['bot-box']}>
                                <span>{period} <b>({totDays}일)</b></span>
                            </div>
                        </div>
                    </div>
                </CustomTimePicker>
            </SheetOverlay>
        </>
    );
};

export default RecordAlarmSettingBox;